import { Alert, Button, Card, Grid, Text } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconClock } from '@tabler/icons-react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import getErrorMessage from '../../helpers/getErrorMessage';
import useFetch from '../../helpers/useFetch';
import { setKlaviyoAccount } from '../../store/klaviyoSlice';
import ErrorMessage from '../ErrorMessage';

const SETUP_ERROR_FALLBACK = 'Could not set up the Klaviyo send metrics. Please try again or contact support.';

// Per-kind delivery-flow provisioning outcome from the /send-metrics response
// ({ marketing: { status, flowId }, transactional: { ... } }) - the endpoint exists to
// make setup observable, so every status the provisioner reports gets a readable line.
const FLOW_STATUS_LABELS = {
  live: { color: 'green', label: 'live' },
  metricJustCreated: { color: 'blue', label: 'metric just created - click again to provision the flow' },
  paused: { color: 'yellow', label: 'paused in Klaviyo - set it Live to resume delivery' },
  failed: { color: 'red', label: 'provisioning failed - check the Klaviyo account' },
  inconclusive: { color: 'gray', label: 'could not be determined (missing permissions?)' },
  contended: { color: 'gray', label: 'another setup run is in progress - try again shortly' },
};

const FLOW_KIND_LABELS = {
  marketing: 'Marketing delivery flow',
  transactional: 'Transactional delivery flow',
};

// Render in a fixed order so the two lines never swap places between clicks (the response
// is an object, so its key order is not something to rely on), with any kind we do not
// know about yet appended alphabetically rather than dropped.
const FLOW_KIND_ORDER = ['marketing', 'transactional'];

const orderedFlowKinds = (flows) => {
  const kinds = Object.keys(flows);
  return [
    ...FLOW_KIND_ORDER.filter((kind) => kinds.includes(kind)),
    ...kinds.filter((kind) => !FLOW_KIND_ORDER.includes(kind)).sort(),
  ];
};

// approvalState -> alert. `true` = approved, `false` = Klaviyo still holds it "Under
// review", anything else = never checked (or the last check could not determine it).
const TRANSACTIONAL_STATUS = {
  true: {
    color: 'green',
    icon: <IconCheck size={16} />,
    text: <>Transactional sending is approved - transactional campaigns will send.</>,
  },
  false: {
    color: 'yellow',
    icon: <IconClock size={16} />,
    text: (
      <>
        Transactional sending is still <b>Under review</b> in Klaviyo. Transactional campaigns are paused until your
        Klaviyo account is approved for transactional email. Marketing campaigns are unaffected.
      </>
    ),
  },
  unknown: {
    color: 'gray',
    icon: <IconAlertTriangle size={16} />,
    text: (
      <>
        Transactional status not determined yet. Click the button (twice if a metric was just created) to set up the
        transactional flow and check its status.
      </>
    ),
  },
};

// One-click Klaviyo delivery setup + transactional-status check. A Klaviyo metric only
// exists after its first event, and the editor test-send only mints the marketing metric
// - so the transactional delivery flow can never be created until its metric exists. This
// button mints any missing send metric (throwaway event, no email delivered), provisions
// the delivery flows, and reads whether the account is approved for transactional
// sending (Klaviyo holds it "Under review" until approved). Safe to run repeatedly.
// `transactionalApproved`: null = unknown/not set up, true = approved, false = under review.
const KlaviyoSendMetricsButton = ({ transactionalApproved }) => {
  const fetch = useFetch();
  const dispatch = useDispatch();
  const klaviyoAccount = useSelector((state) => state.klaviyo.account);
  const { accountId, domainId } = useParams();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState();

  // Prefer the fresh value from the last click; fall back to the persisted account value.
  // `??` (not a truthiness check) so an explicit `false` from the response is kept - useFetch
  // resolves `{}` for a 202/204 or an unparseable 200 body, and a truthy-but-empty result must
  // not flip an approved account back to "not determined yet".
  const approvalState = result?.transactionalApproved ?? transactionalApproved;

  const createMetrics = async () => {
    setError(undefined);
    setResult(undefined);
    setLoading(true);
    try {
      const response = await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/klaviyo/send-metrics`, {
        method: 'POST',
      });
      setResult(response);
      // Keep Redux authoritative (same as KlaviyoDisconnectButton): the campaign and workflow
      // editors gate the Transactional scope on state.klaviyo.account.transactionalApproved,
      // so without this the scope stays disabled until the next full domain load even though
      // this card already says "approved".
      if (klaviyoAccount && typeof response?.transactionalApproved === 'boolean') {
        dispatch(setKlaviyoAccount({ ...klaviyoAccount, transactionalApproved: response.transactionalApproved }));
      }
    } catch (e) {
      // The likeliest failure here is a private key missing flows:write / templates:write -
      // the backend names the missing scope, and that is exactly what the admin needs to act on.
      setError(getErrorMessage(e, SETUP_ERROR_FALLBACK));
    } finally {
      setLoading(false);
    }
  };

  const status = TRANSACTIONAL_STATUS[approvalState === true || approvalState === false ? approvalState : 'unknown'];
  const transactionalStatus = (
    <Alert color={status.color} icon={status.icon} mt="xs" variant="light">
      <Text size="sm">{status.text}</Text>
    </Alert>
  );

  return (
    <Card withBorder>
      <Card.Section inheritPadding py="sm">
        <Grid>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              Send metrics
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Text size="sm" c="dimmed" mb="xs">
              Creates the "Releva Send Email" and "Releva Send Email (Transactional)" metrics in Klaviyo, provisions
              their delivery flows, and checks whether transactional sending is approved. Safe to run more than once.
            </Text>
            <Button variant="default" onClick={createMetrics} loading={loading}>
              Set up send metrics &amp; check transactional status
            </Button>

            {result && (
              <Text size="sm" c="green" mt="xs">
                {result.minted?.length ? `Created: ${result.minted.join(', ')}. ` : ''}
                {result.existing?.length ? `Already present: ${result.existing.join(', ')}.` : ''}
                {!result.minted?.length && !result.existing?.length ? 'Done.' : ''}
              </Text>
            )}

            {result?.flows &&
              orderedFlowKinds(result.flows).map((kind) => {
                const flow = result.flows[kind];
                // hasOwn, not a truthiness check: a status of "toString" would otherwise
                // resolve to an inherited function and render an empty coloured line.
                const flowStatus = Object.hasOwn(FLOW_STATUS_LABELS, flow?.status)
                  ? FLOW_STATUS_LABELS[flow.status]
                  : { color: 'gray', label: flow?.status || 'unknown' };
                return (
                  <Text size="sm" key={kind} mt={4}>
                    {Object.hasOwn(FLOW_KIND_LABELS, kind) ? FLOW_KIND_LABELS[kind] : kind}:{' '}
                    <Text span c={flowStatus.color}>
                      {flowStatus.label}
                    </Text>
                    {flow?.flowId ? (
                      <Text span c="dimmed">
                        {' '}
                        ({flow.flowId})
                      </Text>
                    ) : null}
                  </Text>
                );
              })}

            {transactionalStatus}

            <ErrorMessage message={error} />
          </Grid.Col>
        </Grid>
      </Card.Section>
    </Card>
  );
};

export default KlaviyoSendMetricsButton;
